/**
 * composables/useAuth.js
 * Reactive authentication state composable.
 *
 * Provides:
 *   user        - Ref<SwaUser|null> - current authenticated user
 *   isLoaded    - Ref<boolean>      - true once the auth check has completed
 *   isAdmin     - ComputedRef<boolean>
 *   isExec      - ComputedRef<boolean>
 *   isPm        - ComputedRef<boolean>
 *   loadUser()  - (re)load user from /.auth/me
 *   loginUrl    - computed login URL
 *   logoutUrl   - computed logout URL
 *
 * Uses module-level refs so auth state is shared across all composable calls
 * (singleton pattern within the module).
 */

import { ref, computed } from 'vue'
import {
  fetchCurrentUser,
  hasAdminRole,
  hasExecRole,
  hasPmRole,
  getLoginUrl,
  getLogoutUrl,
} from '@/services/auth.js'

// ---- Shared state (module singleton) ----

/** @type {import('vue').Ref<import('@/services/auth.js').SwaUser|null>} */
const user = ref(null)

/** True once the initial auth fetch has completed (success or failure). */
const isLoaded = ref(false)

/** Tracks in-flight loadUser call to avoid duplicate requests. */
let loadPromise = null

// ---- Composable ----

/**
 * Returns shared reactive auth state and helpers.
 *
 * @returns {{
 *   user: import('vue').Ref,
 *   isLoaded: import('vue').Ref<boolean>,
 *   isAdmin: import('vue').ComputedRef<boolean>,
 *   isExec: import('vue').ComputedRef<boolean>,
 *   isPm: import('vue').ComputedRef<boolean>,
 *   loadUser: (options?: object) => Promise<void>,
 *   loginUrl: import('vue').ComputedRef<string>,
 *   logoutUrl: import('vue').ComputedRef<string>,
 * }}
 */
export function useAuth() {
  /**
   * Load (or reload) the current user from SWA's /.auth/me endpoint.
   * De-duplicates concurrent calls -- only one fetch runs at a time.
   *
   * @returns {Promise<void>}
   */
  async function loadUser(options = {}) {
    if (loadPromise) return loadPromise

    loadPromise = (async () => {
      try {
        user.value = await fetchCurrentUser(options)
      } catch (err) {
        console.warn('[useAuth] loadUser error:', err)
        user.value = null
      } finally {
        isLoaded.value = true
        loadPromise = null
      }
    })()

    return loadPromise
  }

  /** True when user has the admin role. */
  const isAdmin = computed(() => hasAdminRole(user.value))

  /** True when user has exec or admin role. */
  const isExec = computed(() => hasExecRole(user.value))

  /** True when user has pm or admin role. */
  const isPm = computed(() => hasPmRole(user.value))

  /** Login URL pointing back to /dashboard after auth. */
  const loginUrl = computed(() => getLoginUrl('/dashboard'))

  /** Logout URL returning to /login. */
  const logoutUrl = computed(() => getLogoutUrl('/login'))

  return {
    user,
    isLoaded,
    isAdmin,
    isExec,
    isPm,
    loadUser,
    loginUrl,
    logoutUrl,
  }
}
